const parseSampleRate = (
  value: string | undefined,
  fallback: number
): number => {
  if (!value) return fallback

  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1
    ? parsed
    : fallback
}

// Read through a static map rather than import.meta.env[key]. A computed
// member access forces Vite to inline the WHOLE env object into the client
// bundle, which on Vercel means every VITE_VERCEL_* system variable ships to
// the browser — project id, deployment id, repo owner/slug, branch URL.
const CLIENT_ENV: Record<string, string | undefined> = {
  VITE_MEDUSA_BACKEND_URL: import.meta.env.VITE_MEDUSA_BACKEND_URL,
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  VITE_SENTRY_RELEASE: import.meta.env.VITE_SENTRY_RELEASE,
  VITE_SENTRY_SEND_DEFAULT_PII: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII,
  VITE_SENTRY_TRACES_SAMPLE_RATE: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
  VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE:
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE:
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
}

const getOptionalEnv = (key: string) => {
  const value = CLIENT_ENV[key]
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const getTracePropagationTargets = () => {
  const targets: Array<string | RegExp> = [/^\/medusa/]
  const medusaBackendUrl = getOptionalEnv("VITE_MEDUSA_BACKEND_URL")

  if (typeof window !== "undefined") {
    targets.push(new RegExp(`^${escapeRegExp(window.location.origin)}/medusa`))
  }

  if (medusaBackendUrl) {
    targets.push(medusaBackendUrl)
  }

  return targets
}

// The Facebook and Instagram in-app browsers inject their own scripts into
// every page (iabjs://navigation_performance_logger_android and friends).
// When the app tears down its WebView mid-report, those scripts throw
// "Error invoking postMessage: Java object is gone" as unhandled errors that
// land in our project. They are not our code and nothing here can fix them.
// denyUrls drops anything thrown from an iabjs:// frame; ignoreErrors catches
// the same message when it arrives without a stack trace.
//
// On iOS the injected script is attributed to the page itself (frames point
// at line 1 of our HTML, inside <link> tags), so denyUrls can't see it. It
// throws reading window.webkit.messageHandlers, the native bridge, which this
// storefront never uses — matching the message is the only handle.
//
// Browser extensions run their own scripts inside our pages too. Errors thrown
// from an extension script have its scheme on the frame, which denyUrls drops.
// Safari's "Invalid call to runtime.sendMessage(). Tab not found." arrives as
// an unhandled rejection with no stack (an extension messaging a tab that has
// closed), so it needs a message match; runtime.sendMessage is an extension
// API this storefront never calls.
export const SENTRY_CLIENT_FILTERS = {
  denyUrls: [
    /^iabjs:\/\//,
    /^(?:safari-web-extension|safari-extension|chrome-extension|moz-extension|ms-browser-extension):\/\//,
  ],
  ignoreErrors: [
    /Java object is gone/,
    /webkit\.messageHandlers/,
    /Invalid call to runtime\.sendMessage\(\)/,
  ],
}

export const getSentryClientConfig = () => {
  const dsn = getOptionalEnv("VITE_SENTRY_DSN")

  if (!dsn) {
    return null
  }

  return {
    dsn,
    environment: getOptionalEnv("VITE_SENTRY_ENVIRONMENT") ?? import.meta.env.MODE,
    release: getOptionalEnv("VITE_SENTRY_RELEASE"),
    sendDefaultPii: getOptionalEnv("VITE_SENTRY_SEND_DEFAULT_PII") === "true",
    tracesSampleRate: parseSampleRate(
      getOptionalEnv("VITE_SENTRY_TRACES_SAMPLE_RATE"),
      import.meta.env.DEV ? 1 : 0.1
    ),
    replaysSessionSampleRate: parseSampleRate(
      getOptionalEnv("VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE"),
      import.meta.env.DEV ? 0 : 0.1
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      getOptionalEnv("VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE"),
      1
    ),
    tracePropagationTargets: getTracePropagationTargets(),
    ...SENTRY_CLIENT_FILTERS,
  }
}
